import axios from 'axios';
import { toast } from 'sonner';

const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || 'https://api.residualvault.com/api/v1',  // ← Changed default
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000,
});

// Request interceptor
api.interceptors.request.use(
  (config) => {
    // Add any custom request logic here
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor
api.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Handle 401 Unauthorized
    if (error.response?.status === 401 && !originalRequest._retry) {
      const errorCode = error.response?.data?.error?.code;
      
      // If token is invalid (not just expired), clear everything and re-login
      if (errorCode === 'INVALID_TOKEN' || errorCode === 'UNAUTHORIZED') {
        localStorage.clear();
        delete api.defaults.headers.common['Authorization'];
        window.location.href = '/login?reason=session_expired';
        return Promise.reject(error);
      }

      originalRequest._retry = true;

      try {
        // Try to refresh token
        const refreshToken = localStorage.getItem('refreshToken');
        if (refreshToken) {
          const response = await api.post('/auth/refresh', { refreshToken });
          const { accessToken } = response.data.data;

          // Update token
          api.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
          originalRequest.headers['Authorization'] = `Bearer ${accessToken}`;

          return api(originalRequest);
        } else {
          // No refresh token - force re-login
          localStorage.clear();
          window.location.href = '/login?reason=session_expired';
          return Promise.reject(error);
        }
      } catch (refreshError) {
        // Refresh failed, logout user
        localStorage.clear();
        delete api.defaults.headers.common['Authorization'];
        window.location.href = '/login?reason=session_expired';
        return Promise.reject(refreshError);
      }
    }

    // Handle specific error codes
    const errorMessage = error.response?.data?.error?.message || 'An error occurred';
    const errorCode = error.response?.data?.error?.code;

    // Skip toast for background/tracking requests
    const skipToastUrls = ['/staking/track-click'];
    const requestUrl = error.config?.url || '';
    if (skipToastUrls.some(url => requestUrl.includes(url))) {
      return Promise.reject(error);
    }

    // Show toast for user-facing errors
    if (error.response?.status >= 400 && error.response?.status < 500) {
      if (errorCode === 'TIER_REQUIRED') {
        toast.error('Upgrade Required', {
          description: errorMessage,
          action: {
            label: 'Upgrade',
            onClick: () => window.location.href = '/pricing',
          },
        });
      } else if (errorCode === 'RATE_LIMIT_EXCEEDED') {
        toast.error('Rate Limit Exceeded', {
          description: 'Please wait a moment and try again',
        });
      } else {
        toast.error(errorMessage);
      }
    } else if (error.response?.status >= 500) {
      toast.error('Server Error', {
        description: 'Something went wrong. Please try again later.',
      });
    } else if (error.code === 'ECONNABORTED') {
      toast.error('Request Timeout', {
        description: 'The request took too long. Please try again.',
      });
    }

    return Promise.reject(error);
  }
);

export default api;
